import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Chip,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  ClickAwayListener,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Add,
  Close,
} from '@mui/icons-material';
import {
  getDropnetLocationsAPI,
  getDropnetRulesAPI,
  getDropnetRuleByIdAPI,
  updateDropnetRuleAPI,
  saveDropnetRuleAPI,
} from '../operations/dropnetService';

const toArray = (val: string | null | undefined): string[] =>
  val ? String(val).split(',').map((s) => { const t = s.trim(); return t.toLowerCase() === 'all' ? 'ALL' : t; }).filter(Boolean) : [];

/* ================= TYPES ================= */
interface DealCodeData {
  id: number;
  scheme_name: string;
  channel: string;
  supplier_type: string;
  supplier: string;
  supplier_name: string; // Added to fix type error
  office_id: string;
  content_type: string;
  currency: string;
  route_type: string;
  applicable_route_type: string;
  from_country: string;
  to_country: string;
  from_region: string | null;
  to_region: string | null;
  excluded_country: string | null;
  excluded_from_country: string | null;
  excluded_to_country: string | null;
  from_city: string;
  to_city: string;
  excluded_city: string | null;
  excluded_from_city: string | null;
  excluded_to_city: string | null;
  airline_name: string;
  cabin_class: string;
  rbd_type: string;
  rbd_code: string;
  schema_type: string;
  fare_tourcode: string;
  farebase: string;
  farebase_matchtype: string;
  code_share: string;
  sales_start: string;
  sales_end: string;
  travel_start: string;
  travel_end: string;
  blackout_startdate: string;
  blackout_enddate: string;
  payout_onward_type: string;
  payout_onward_value: string;
  payout_chargevalue: string;
  payout_return_type: string;
  payout_return_value: string;
  payout_onward?: { type: string; chargeValue: string; paxtype: string; amount: string }[];
  payout_return?: { type: string; chargeValue: string; paxtype: string; amount: string }[];
  is_active: number;
  created_at?: string;
  updated_at?: string;
}

interface PayoutOnwardRow {
  payout_onward_type: string;
  payout_chargevalue: string;
  payout_onward_value: string;
  payout_onward_amount: string;
}

const EMPTY_PAYOUT_ONWARD_ROW: PayoutOnwardRow = {
  payout_onward_type: '',
  payout_chargevalue: '',
  payout_onward_value: '',
  payout_onward_amount: '',
};

const MAX_PAYOUT_ONWARD_ROWS = 3;

interface PayoutReturnRow {
  payout_return_type: string;
  payout_return_chargevalue: string;
  payout_return_value: string;
  payout_return_amount: string;
}

const EMPTY_PAYOUT_RETURN_ROW: PayoutReturnRow = {
  payout_return_type: '',
  payout_return_chargevalue: '',
  payout_return_value: '',
  payout_return_amount: '',
};

const MAX_PAYOUT_RETURN_ROWS = 3;

const STATIC_ALL_EXPANSIONS: [string, string][] = [
  ['GDS,LCC,NDC', 'All'],
  ['Published,Private', 'All'],
  ['OneWay,RoundTrip', 'Both'],
  ['International,Domestic', 'All'],
  ['ADULT,CHILD', 'All'],
  ['A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W,X,Y,Z', 'All'],
];

const FlightDropnet = () => {
  const [openRowId, setOpenRowId] = useState<number | null>(null);
  const [editRowId, setEditRowId] = useState<number | null>(null);
  const [dealCodes, setDealCodes] = useState<DealCodeData[]>([]);
  const [editedData, setEditedData] = useState<Partial<DealCodeData>>({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [viewLoading, setViewLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [viewDataMap, setViewDataMap] = useState<Record<number, DealCodeData>>({});
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMsg, setSnackbarMsg] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("success");
  const [newDealErrors, setNewDealErrors] = useState<Record<string, string>>({});
  const [showAllModal, setShowAllModal] = useState<{ open: boolean; title: string; items: string[] }>({
    open: false, title: '', items: [],
  });

  const emptyDeal: Partial<DealCodeData> = {
    scheme_name: '',
    channel: '',
    supplier_type: '',
    supplier: '',
    office_id: '',
    content_type: '',
    currency: 'USD',
    route_type: '',
    applicable_route_type: '',
    from_country: '',
    to_country: '',
    from_region: null,
    to_region: null,
    excluded_country: null,
    excluded_from_country: null,
    excluded_to_country: null,
    from_city: '',
    to_city: '',
    excluded_city: null,
    excluded_from_city: null,
    excluded_to_city: null,
    airline_name: '',
    cabin_class: '',
    rbd_type: '',
    rbd_code: '',
    schema_type: '',
    fare_tourcode: '',
    farebase: '',
    farebase_matchtype: '',
    code_share: '',
    sales_start: '',
    sales_end: '',
    travel_start: '',
    travel_end: '',
    blackout_startdate: '',
    blackout_enddate: '',
    payout_onward_type: '',
    payout_onward_value: '',
    payout_chargevalue: '',
    payout_return_type: '',
    payout_return_value: '',
    is_active: '' as any,
  };

  const [newDeal, setNewDeal] = useState<Partial<DealCodeData>>({ ...emptyDeal });
  const [payoutOnwardRows, setPayoutOnwardRows] = useState<PayoutOnwardRow[]>([{ ...EMPTY_PAYOUT_ONWARD_ROW }]);

  /* ================= PAYOUT ONWARD ROW HELPERS ================= */
  const addPayoutOnwardRow = () => {
    if (payoutOnwardRows.length < MAX_PAYOUT_ONWARD_ROWS) {
      setPayoutOnwardRows((prev) => [...prev, { ...EMPTY_PAYOUT_ONWARD_ROW }]);
    }
  };

  const removePayoutOnwardRow = (index: number) => {
    setPayoutOnwardRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePayoutOnwardRowChange = (index: number, field: keyof PayoutOnwardRow, value: string) => {
    setPayoutOnwardRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
    const suffix = field === 'payout_onward_type' ? 'type' : field === 'payout_chargevalue' ? 'chargevalue' : field === 'payout_onward_value' ? 'value' : 'amount';
    setNewDealErrors((prev) => { const n = { ...prev }; delete n[`payout_onward_${index}_${suffix}`]; return n; });
  };

  const [payoutReturnRows, setPayoutReturnRows] = useState<PayoutReturnRow[]>([{ ...EMPTY_PAYOUT_RETURN_ROW }]);

  /* ================= PAYOUT RETURN ROW HELPERS ================= */
  const addPayoutReturnRow = () => {
    if (payoutReturnRows.length < MAX_PAYOUT_RETURN_ROWS) {
      setPayoutReturnRows((prev) => [...prev, { ...EMPTY_PAYOUT_RETURN_ROW }]);
    }
  };

  const removePayoutReturnRow = (index: number) => {
    setPayoutReturnRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePayoutReturnRowChange = (index: number, field: keyof PayoutReturnRow, value: string) => {
    setPayoutReturnRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
    const suffix = field === 'payout_return_type' ? 'type' : field === 'payout_return_chargevalue' ? 'chargevalue' : field === 'payout_return_value' ? 'value' : 'amount';
    setNewDealErrors((prev) => { const n = { ...prev }; delete n[`payout_return_${index}_${suffix}`]; return n; });
  };

  /* ================= SNACKBAR HELPERS ================= */
  const showSnackbar = (message: string, severity: "success" | "error" | "info" | "warning" = "success") => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const openShowAllModal = (title: string, rawValue: string | null) => {
    const items = rawValue ? rawValue.split(',').map((s) => s.trim()).filter(Boolean).sort((a, b) => a.localeCompare(b)) : [];
    setShowAllModal({ open: true, title, items });
  };

  const closeShowAllModal = () => {
    setShowAllModal((prev) => ({ ...prev, open: false }));
  };

  /* ================= LOCATIONS AUTOCOMPLETE ================= */
  interface CountryItem {
    name: string;
    region?: string;
  }

  interface CityItem {
    name: string;
    country: string | null;
    region: string | null;
  }

  interface SupplierItem {
    supplier_source: string;
    supplier_name: string;
    supplier_code: number;
    office_id: string | null;
  }

  interface LocationsData {
    countries: CountryItem[];
    cities: CityItem[];
    regions: string[];
    airlines: string[];
    suppliers: SupplierItem[];
  }

  const locationsCache = useRef<LocationsData | null>(null);
  const locationsParamCache = useRef<Map<string, any>>(new Map());
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
  const [activeAutocompleteField, setActiveAutocompleteField] = useState<string | null>(null);
  const [multiSelectQuery, setMultiSelectQuery] = useState<Record<string, string>>({});
  const [autocompleteInputDraft, setAutocompleteInputDraft] = useState<Record<string, string>>({});
  const [availableSuppliers, setAvailableSuppliers] = useState<SupplierItem[]>([]);

  const fetchLocations = useCallback(async (): Promise<LocationsData | null> => {
    if (locationsCache.current) return locationsCache.current;
    try {
      setLocationsLoading(true);
      const response = await getDropnetLocationsAPI();
      const result = response?.data ?? response ?? {};
      locationsCache.current = {
        countries: result.countries ?? [],
        cities: (result.cities ?? []).map((c: any) =>
          typeof c === 'string' ? { name: c, country: null, region: null } : c
        ),
        regions: result.regions ?? [],
        airlines: (result.airlines ?? []).filter((v: string) => v.toLowerCase() !== 'all'),
        suppliers: result.suppliers ?? [],
      };
      setAvailableSuppliers(result.suppliers ?? []);
      return locationsCache.current;
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      return null;
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  const fetchLocationsByParams = useCallback(async (params: { region?: string; country?: string }): Promise<any> => {
    const queryParts: string[] = [];
    if (params.region) queryParts.push(`region=${encodeURIComponent(params.region)}`);
    if (params.country) queryParts.push(`country=${encodeURIComponent(params.country)}`);
    const cacheKey = queryParts.join('&') || 'all';
    if (locationsParamCache.current.has(cacheKey)) return locationsParamCache.current.get(cacheKey);
    try {
      setLocationsLoading(true);
      const response = await getDropnetLocationsAPI(params);
      const result = response?.data ?? response ?? {};
      locationsParamCache.current.set(cacheKey, result);
      return result;
    } catch (err) {
      console.error('Failed to fetch locations with params:', err);
      return null;
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  const getLocationCategory = (field: keyof DealCodeData): keyof LocationsData | null => {
    if (['from_country', 'to_country', 'excluded_from_country', 'excluded_to_country'].includes(field)) return 'countries';
    if (['from_city', 'to_city', 'excluded_city', 'excluded_from_city', 'excluded_to_city'].includes(field)) return 'cities';
    if (['from_region', 'to_region'].includes(field)) return 'regions';
    if (field === 'airline_name') return 'airlines';
    return null;
  };

  const handleAutocompleteInput = async (
    field: keyof DealCodeData,
    value: string,
    onChange: (field: keyof DealCodeData, value: any) => void,
    formData?: Partial<DealCodeData>,
  ) => {
    setAutocompleteInputDraft((prev) => ({ ...prev, [field as string]: value }));
    const category = getLocationCategory(field);
    if (!category) return;

    if (category === 'countries') {
      const regionField = (field === 'from_country' || field === 'excluded_from_country')
        ? 'from_region' : 'to_region';
      const selectedRegions = String(formData?.[regionField] ?? '')
        .split(',').map((r) => r.trim()).filter(Boolean);
      const specificRegions = selectedRegions.filter((r) => r.toLowerCase() !== 'all');
      let countries: CountryItem[];
      if (specificRegions.length > 0) {
        const results = await Promise.all(specificRegions.map((r) => fetchLocationsByParams({ region: r })));
        const seen = new Set<string>();
        countries = results.flatMap((r) => (r?.countries ?? []) as CountryItem[]).filter((item) => {
          if (seen.has(item.name)) return false;
          seen.add(item.name);
          return true;
        });
      } else {
        const result = await fetchLocationsByParams({});
        countries = result?.countries ?? [];
      }
      const fieldsAllowingAll: Array<keyof DealCodeData> = ['from_country', 'to_country'];
      const filtered = countries
        .filter((item) => {
          if (item.name.toLowerCase() === 'all') return fieldsAllowingAll.includes(field);
          if (value.length >= 3) return item.name.toLowerCase().startsWith(value.toLowerCase());
          return true;
        })
        .map((item) => item.name);
      setLocationSuggestions([...filtered].sort((a, b) => {
        if (a.toLowerCase() === 'all') return -1;
        if (b.toLowerCase() === 'all') return 1;
        return a.localeCompare(b);
      }));
      setActiveAutocompleteField(field);
      return;
    }

    if (category === 'cities') {
      const isFrom = ['from_city', 'excluded_from_city', 'excluded_city'].includes(field);
      const countryField = isFrom ? 'from_country' : 'to_country';
      const selectedCountryRaw = String(formData?.[countryField] ?? '').trim();
      const specificCountries = selectedCountryRaw
        .split(',').map((c) => c.trim()).filter((c) => c && c.toLowerCase() !== 'all');
      const locData = await fetchLocations();
      if (!locData) return;
      let cityItems = locData.cities.filter((c) => c.name.toLowerCase() !== 'all');
      if (specificCountries.length > 0) {
        const countrySet = new Set(specificCountries.map((c) => c.toLowerCase()));
        cityItems = cityItems.filter((c) => c.country && countrySet.has(c.country.toLowerCase()));
      }
      const fieldsAllowingAll: Array<keyof DealCodeData> = ['from_city', 'to_city'];
      const names = Array.from(new Set(cityItems.map((c) => c.name)));
      const filtered = names.filter((name) => {
        if (value.length >= 3) return name.toLowerCase().startsWith(value.toLowerCase());
        return true;
      });
      const sorted = [...filtered].sort((a, b) => a.localeCompare(b));
      setLocationSuggestions(fieldsAllowingAll.includes(field) ? ['All', ...sorted] : sorted);
      setActiveAutocompleteField(field);
      return;
    }

    const locData = await fetchLocations();
    if (!locData) return;
    const filtered = (locData[category] as string[]).filter((item) => {
      if (item.toLowerCase() === 'all') return false;
      const [airlineName, airlineCode] = item.split(',').map((s) => s.trim());
      const code = (airlineCode ?? '').toLowerCase();
      const name = airlineName.toLowerCase();
      const query = value.toLowerCase();
      if (value.length >= 3) return name.startsWith(query) || code.startsWith(query);
      if (value.length === 2) return code === query;
      return true;
    });
    setLocationSuggestions([...filtered].sort((a, b) => a.localeCompare(b)));
    setActiveAutocompleteField(field);
  };

  const handleSuggestionSelect = (
    field: keyof DealCodeData,
    value: string,
    onChange: (field: keyof DealCodeData, value: any) => void,
  ) => {
    onChange(field, value);
    setAutocompleteInputDraft((prev) => ({ ...prev, [field as string]: value }));
    setLocationSuggestions([]);
    setActiveAutocompleteField(null);
  };

  const handleMultiSelectInput = async (field: keyof DealCodeData, value: string, formData?: Partial<DealCodeData>) => {
    setMultiSelectQuery((prev) => ({ ...prev, [field as string]: value }));
    const category = getLocationCategory(field);
    if (!category) return;

    if (category === 'countries') {
      const regionField = (field === 'from_country' || field === 'excluded_from_country')
        ? 'from_region' : 'to_region';
      const selectedRegions = String(formData?.[regionField] ?? '')
        .split(',').map((r) => r.trim()).filter(Boolean);
      const specificRegions = selectedRegions.filter((r) => r.toLowerCase() !== 'all');
      let countries: CountryItem[];
      if (specificRegions.length > 0) {
        const results = await Promise.all(specificRegions.map((r) => fetchLocationsByParams({ region: r })));
        const seen = new Set<string>();
        countries = results.flatMap((r) => (r?.countries ?? []) as CountryItem[]).filter((item) => {
          if (seen.has(item.name)) return false;
          seen.add(item.name);
          return true;
        });
      } else {
        const result = await fetchLocationsByParams({});
        countries = result?.countries ?? [];
      }
      const countryAllowAll = field === 'from_country' || field === 'to_country';
      const filtered = Array.from(new Set(
        countries
          .filter((item) => {
            if (item.name.toLowerCase() === 'all') return false;
            if (value.length >= 3) return item.name.toLowerCase().startsWith(value.toLowerCase());
            return true;
          })
          .map((item) => item.name)
      ));
      const sorted = [...filtered].sort((a, b) => a.localeCompare(b));
      setLocationSuggestions(countryAllowAll ? ['All', ...sorted] : sorted);
      setActiveAutocompleteField(field);
      return;
    }

    if (category === 'cities') {
      const isFrom = ['from_city', 'excluded_from_city', 'excluded_city'].includes(field);
      const countryField = isFrom ? 'from_country' : 'to_country';
      const selectedCountryRaw = String(formData?.[countryField] ?? '').trim();
      const specificCountries = selectedCountryRaw
        .split(',').map((c) => c.trim()).filter((c) => c && c.toLowerCase() !== 'all');
      const locData = await fetchLocations();
      if (!locData) return;
      let cityItems = locData.cities.filter((c) => c.name.toLowerCase() !== 'all');
      if (specificCountries.length > 0) {
        const countrySet = new Set(specificCountries.map((c) => c.toLowerCase()));
        cityItems = cityItems.filter((c) => c.country && countrySet.has(c.country.toLowerCase()));
      }
      const cityAllowAll = field === 'from_city' || field === 'to_city';
      const names = Array.from(new Set(cityItems.map((c) => c.name)));
      const filtered = names.filter((name) => {
        if (value.length >= 3) return name.toLowerCase().startsWith(value.toLowerCase());
        return true;
      });
      const sorted = [...filtered].sort((a, b) => a.localeCompare(b));
      setLocationSuggestions(cityAllowAll ? ['All', ...sorted] : sorted);
      setActiveAutocompleteField(field);
      return;
    }

    const locData = await fetchLocations();
    if (!locData) return;
    const allowAll = field === 'from_region' || field === 'to_region';
    const filtered = Array.from(new Set((locData[category] as string[]).filter((item: string) => {
      if (item.toLowerCase() === 'all') return false; // handled separately below
      if (value.length >= 3) return item.toLowerCase().startsWith(value.toLowerCase());
      return true;
    })));
    const sorted = [...filtered].sort((a, b) => a.localeCompare(b));
    setLocationSuggestions(allowAll ? ['All', ...sorted] : sorted);
    setActiveAutocompleteField(field);
  };

  const renderAutocompleteField = (
    label: string,
    field: keyof DealCodeData,
    data: Partial<DealCodeData>,
    onChange: (field: keyof DealCodeData, value: any) => void,
    errors?: Record<string, string>,
  ) => {
    const isActive = activeAutocompleteField === field;
    const inputValue = field as string in autocompleteInputDraft
      ? autocompleteInputDraft[field as string]
      : String(data[field] ?? '');
    return (
      <ClickAwayListener onClickAway={() => {
        if (isActive) {
          onChange(field, '');
          setAutocompleteInputDraft((prev) => ({ ...prev, [field as string]: '' }));
          setActiveAutocompleteField(null);
          setLocationSuggestions([]);
        }
      }}>
        <Box className="form-control inputFieldWrap" sx={{ position: 'relative' }}>
          <label>{label}</label>
          <input
            type="text"
            className="inputField"
            style={errors && errors[field] ? { border: '1px solid #d32f2f' } : undefined}
            value={inputValue}
            onChange={(e) => handleAutocompleteInput(field, e.target.value, onChange, data)}
            onFocus={() => {
              const currentVal = String(data[field] ?? '');
              setAutocompleteInputDraft((prev) => ({
                ...prev,
                [field as string]: prev[field as string] ?? currentVal,
              }));
              const draft = autocompleteInputDraft[field as string] ?? currentVal;
              handleAutocompleteInput(field, draft, onChange, data);
            }}
          />
          {isActive && locationSuggestions.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 100,
                maxHeight: 200,
                overflowY: 'auto',
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '0 0 8px 8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              {locationSuggestions.map((item, idx) => (
                <Box
                  key={idx}
                  onClick={() => handleSuggestionSelect(field, item, onChange)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#272727',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    borderBottom: idx < locationSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                  }}
                >
                  {item}
                </Box>
              ))}
            </Box>
          )}
          {isActive && locationsLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 100,
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '0 0 8px 8px',
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: '14px', color: '#888' }}>Loading...</Typography>
            </Box>
          )}
          {errors && errors[field] && (
            <span className='errorNewSupplier'>{errors[field]}</span>
          )}
        </Box>
      </ClickAwayListener>
    );
  };

  const renderMultiSelectAutocomplete = (
    label: string,
    field: keyof DealCodeData,
    data: Partial<DealCodeData>,
    onChange: (field: keyof DealCodeData, value: any) => void,
    errors?: Record<string, string>,
    disabled?: boolean,
  ) => {
    const isActive = activeAutocompleteField === field;
    const rawRaw = data[field];
    const rawValue = Array.isArray(rawRaw) ? rawRaw.join(',') : (rawRaw != null ? String(rawRaw) : '');
    const selectedValues = rawValue ? rawValue.split(',').map((s) => s.trim()).filter(Boolean) : [];
    const query = multiSelectQuery[field as string] ?? '';
    const allowAll = field === 'from_region' || field === 'to_region' || field === 'from_country' || field === 'to_country' || field === 'from_city' || field === 'to_city';
    const isCountryAllField = field === 'from_country' || field === 'to_country' || field === 'from_city' || field === 'to_city';
    // Use only what's displayed in the dropdown (deduped, excludes "All" row)
    const displayedOptions = allowAll
      ? locationSuggestions.filter((s) => s.toLowerCase() !== 'all')
      : [];
    const allOptionsSelected = allowAll && displayedOptions.length > 0
      && (selectedValues.includes('All') || displayedOptions.every((r) => selectedValues.includes(r)));

    const toggleItem = (item: string) => {
      if (allowAll && item.toLowerCase() === 'all') {
        if (allOptionsSelected) {
          onChange(field, null);
        } else {
          onChange(field, 'All');
        }
      } else {
        // If "All" is currently stored, expand to all options first then remove the clicked item
        const baseSelected = selectedValues.includes('All')
          ? displayedOptions.filter((v) => v !== item)
          : selectedValues.filter((v) => v.toLowerCase() !== 'all').includes(item)
            ? selectedValues.filter((v) => v.toLowerCase() !== 'all').filter((v) => v !== item)
            : [...selectedValues.filter((v) => v.toLowerCase() !== 'all'), item];
        // If all displayed options are now selected, collapse back to "All"
        const allNowSelected = displayedOptions.length > 0 && displayedOptions.every((o) => baseSelected.includes(o));
        if (allNowSelected) {
          onChange(field, 'All');
        } else {
          onChange(field, baseSelected.length > 0 ? baseSelected.join(',') : null);
        }
      }
    };

    const removeChip = (item: string) => {
      // If "All" chip is removed, clear the field
      if (item.toLowerCase() === 'all') {
        onChange(field, null);
        return;
      }
      // If "All" is stored, expand to all options then remove the specific item
      const base = selectedValues.includes('All') ? displayedOptions : selectedValues;
      const newSelected = base.filter((v) => v !== item);
      const allNowSelected = displayedOptions.length > 0 && displayedOptions.every((o) => newSelected.includes(o));
      if (allNowSelected) {
        onChange(field, 'All');
      } else {
        onChange(field, newSelected.length > 0 ? newSelected.join(',') : null);
      }
    };

    return (
      <ClickAwayListener onClickAway={() => {
        if (isActive) {
          setMultiSelectQuery((prev) => ({ ...prev, [field as string]: '' }));
          setActiveAutocompleteField(null);
          setLocationSuggestions([]);
        }
      }}>
        <Box
          className="form-control inputFieldWrap"
          sx={{
            position: 'relative',
            ...(disabled && { opacity: 0.5, pointerEvents: 'none', cursor: 'not-allowed' }),
          }}
        >
          <label>{label}</label>
          <Box
            className="inputField"
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: 0.5,
              height: '42px',
              overflowX: 'auto',
              overflowY: 'hidden',
              padding: '4px 10px',
              cursor: disabled ? 'not-allowed' : 'text',
              '&::-webkit-scrollbar': { height: '2px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#bbb', borderRadius: '2px' },
              ...(errors && errors[field as string] && { border: '1px solid #d32f2f !important' }),
            }}
          >
            {selectedValues.map((val) => (
              <Chip
                key={val}
                label={val}
                size="small"
                onDelete={() => removeChip(val)}
                sx={{ fontSize: '12px', height: 22 }}
              />
            ))}
            <input
              style={{
                border: 'none',
                outline: 'none',
                flex: 1,
                minWidth: '80px',
                background: 'transparent',
                fontSize: '14px',
                color: '#272727',
                padding: 0,
              }}
              value={query}
              placeholder={selectedValues.length === 0 ? 'Click or type to search...' : ''}
              onChange={(e) => handleMultiSelectInput(field, e.target.value, data)}
              onFocus={() => { handleMultiSelectInput(field, query, data); }}
            />
          </Box>
          {isActive && locationSuggestions.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 100,
                maxHeight: 200,
                overflowY: 'auto',
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '0 0 8px 8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              {locationSuggestions.map((item, idx) => (
                <Box
                  key={idx}
                  onClick={() => toggleItem(item)}
                  sx={{
                    px: 2, py: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                    fontSize: '14px', color: '#272727',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    borderBottom: idx < locationSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={
                      allowAll && item.toLowerCase() === 'all'
                        ? allOptionsSelected
                        : (allowAll && selectedValues.includes('All')) || selectedValues.includes(item)
                    }
                    sx={{ p: 0, color: '#272727', '&.Mui-checked': { color: '#272727' } }}
                  />
                  {item}
                </Box>
              ))}
            </Box>
          )}
          {isActive && locationsLoading && (
            <Box
              sx={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                backgroundColor: '#fff', border: '1px solid #e0e0e0',
                borderRadius: '0 0 8px 8px', px: 2, py: 1.5,
                display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: '14px', color: '#888' }}>Loading...</Typography>
            </Box>
          )}
          {errors && errors[field] && (
            <span className='errorNewSupplier'>{errors[field]}</span>
          )}
        </Box>
      </ClickAwayListener>
    );
  };

  /* ================= HELPERS ================= */
  const capitalize = (value?: string | null) => {
    if (!value) return '-';
    return value
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const toInputDate = (dateStr?: string | null) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toISOString().split('T')[0];
    } catch {
      return dateStr;
    }
  };

  /* ================= FETCH ALL API ================= */
  const fetchDealCodes = async () => {
    try {
      setLoading(true);
      const response = await getDropnetRulesAPI();

      const rawData = response?.data ?? response ?? [];
      const normalizedData: DealCodeData[] = (Array.isArray(rawData) ? rawData : []).map(
        (item: any) => ({
          id: item.id,
          scheme_name: item.scheme_name ?? '',
          channel: item.channel ?? '',
          supplier_type: item.supplier_type ?? '',
          supplier: item.supplier ?? '',
          supplier_name: item.supplier_name ?? '', // <-- Added to fix type error
          office_id: item.office_id ?? '',
          content_type: item.content_type ?? '',
          currency: item.currency ?? '',
          route_type: item.route_type ?? '',
          applicable_route_type: item.applicable_route_type ?? '',
          from_country: item.from_country ?? '',
          to_country: item.to_country ?? '',
          from_region: item.from_region ?? null,
          to_region: item.to_region ?? null,
          excluded_country: item.excluded_country ?? null,
          excluded_from_country: item.excluded_from_country ?? null,
          excluded_to_country: item.excluded_to_country ?? null,
          from_city: item.from_city ?? '',
          to_city: item.to_city ?? '',
          excluded_city: item.excluded_city ?? null,
          excluded_from_city: item.excluded_from_city ?? null,
          excluded_to_city: item.excluded_to_city ?? null,
          airline_name: item.airline_name ?? '',
          cabin_class: item.cabin_class ?? '',
          rbd_type: item.rbd_type ?? '',
          rbd_code: item.rbd_code ?? '',
          schema_type: item.schema_type ?? '',
          fare_tourcode: item.fare_tourcode ?? '',
          farebase: item.farebase ?? '',
          farebase_matchtype: item.farebase_matchtype ?? '',
          code_share: item.code_share ?? '',
          sales_start: item.sales_start ?? '',
          sales_end: item.sales_end ?? '',
          travel_start: item.travel_start ?? '',
          travel_end: item.travel_end ?? '',
          blackout_startdate: item.blackout_start ?? item.blackout_startdate ?? '',
          blackout_enddate: item.blackout_end ?? item.blackout_enddate ?? '',
          payout_onward_type: item.payout_onward_type ?? '',
          payout_onward_value: item.payout_onward_value ?? '',
          payout_chargevalue: item.payout_chargevalue ?? '',
          payout_return_type: item.payout_return_type ?? '',
          payout_return_value: item.payout_return_value ?? '',
          is_active: item.is_active === true || item.is_active === 1 ? 1 : 0,
          created_at: item.created_at ?? null,
          updated_at: item.updated_at ?? null,
        })
      );

      setDealCodes(normalizedData);
    } catch (err) {
      console.error('Failed to fetch dropnet:', err);
      showSnackbar('Failed to fetch dropnet', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealCodes();
    fetchLocations();
  }, []);

  /* ================= VIEW API ================= */
  const toggleViewRow = async (id: number) => {
    if (openRowId === id) {
      setOpenRowId(null);
      return;
    }

    setOpenRowId(id);
    setEditRowId(null);

    if (viewDataMap[id]) return;

    try {
      setViewLoading(true);
      const response = await getDropnetRuleByIdAPI({ id });

      const viewResult = response?.data ?? response ?? {};

      setViewDataMap((prev) => ({ ...prev, [id]: viewResult }));
      setDealCodes((prev) =>
        prev.map((d) => (d.id === id ? { ...d, ...viewResult } : d))
      );
    } catch (err) {
      console.error('Failed to fetch dropnet details:', err);
      showSnackbar('Failed to load dropnet details', 'error');
    } finally {
      setViewLoading(false);
    }
  };

  /* ================= EDIT TOGGLE ================= */
  const toggleEditRow = async (id: number, deal: DealCodeData) => {
    if (editRowId === id) {
      setEditRowId(null);
      setEditedData({});
      return;
    }

    setEditRowId(id);
    setOpenRowId(null);
    setIsAddingNew(false);

    let detailedDeal = deal;

    // Fetch view data if payout arrays are not yet loaded (flat fields mix paxtype/amount)
    if (!deal.payout_onward) {
      try {
        const response = await getDropnetRuleByIdAPI({ id });
        const viewResult = response?.data ?? response ?? {};
        detailedDeal = { ...deal, ...viewResult };
        setDealCodes((prev) =>
          prev.map((d) => (d.id === id ? detailedDeal : d))
        );
      } catch {
        // continue with available flat fields
      }
    }

    setEditedData({ ...detailedDeal });

    const onwardArr = detailedDeal.payout_onward;
    setPayoutOnwardRows(
      onwardArr && onwardArr.length > 0
        ? onwardArr.map((p) => ({
            payout_onward_type: p.type ?? '',
            payout_chargevalue: p.chargeValue ?? '',
            payout_onward_value: p.paxtype ?? '',
            payout_onward_amount: p.amount ?? '',
          }))
        : [{
            payout_onward_type: detailedDeal.payout_onward_type ?? '',
            payout_chargevalue: detailedDeal.payout_chargevalue ?? '',
            payout_onward_value: '',
            payout_onward_amount: '',
          }]
    );

    const returnArr = detailedDeal.payout_return;
    setPayoutReturnRows(
      returnArr && returnArr.length > 0
        ? returnArr.map((p) => ({
            payout_return_type: p.type ?? '',
            payout_return_chargevalue: p.chargeValue ?? '',
            payout_return_value: p.paxtype ?? '',
            payout_return_amount: p.amount ?? '',
          }))
        : [{
            payout_return_type: detailedDeal.payout_return_type ?? '',
            payout_return_chargevalue: detailedDeal.payout_chargevalue ?? '',
            payout_return_value: '',
            payout_return_amount: '',
          }]
    );
  };

  const handleCancel = () => {
    setEditRowId(null);
    setEditedData({});
    setPayoutOnwardRows([{ ...EMPTY_PAYOUT_ONWARD_ROW }]);
    setPayoutReturnRows([{ ...EMPTY_PAYOUT_RETURN_ROW }]);
    setAutocompleteInputDraft({});
    setMultiSelectQuery({});
  };

  const handleEditChange = (field: keyof DealCodeData, value: any) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  /* ================= UPDATE API ================= */
  const handleUpdate = async (id: number) => {
    const originalDeal = dealCodes.find((d) => d.id === id);
    if (!originalDeal) return;

    const hasChanges = Object.keys(editedData).some((key) => {
      const field = key as keyof DealCodeData;
      return editedData[field] !== originalDeal[field];
    });

    if (!hasChanges) {
      showSnackbar("Didn't update anything", "info");
      setEditRowId(null);
      setEditedData({});
      return;
    }

    const mergedForValidation = { ...originalDeal, ...editedData };

    if (
      mergedForValidation.from_country && mergedForValidation.to_country &&
      String(mergedForValidation.from_country).toLowerCase() !== 'all' && String(mergedForValidation.to_country).toLowerCase() !== 'all' &&
      String(mergedForValidation.from_country).toLowerCase() === String(mergedForValidation.to_country).toLowerCase()
    ) {
      showSnackbar('From Country and To Country cannot be the same', 'error');
      return;
    }

    if (
      mergedForValidation.from_city && mergedForValidation.to_city &&
      String(mergedForValidation.from_city).toLowerCase() !== 'all' && String(mergedForValidation.to_city).toLowerCase() !== 'all' &&
      String(mergedForValidation.from_city).toLowerCase() === String(mergedForValidation.to_city).toLowerCase()
    ) {
      showSnackbar('From City and To City cannot be the same', 'error');
      return;
    }

    if (
      mergedForValidation.from_region && mergedForValidation.to_region &&
      String(mergedForValidation.from_region).toLowerCase() !== 'all' && String(mergedForValidation.to_region).toLowerCase() !== 'all' &&
      String(mergedForValidation.from_region).toLowerCase() === String(mergedForValidation.to_region).toLowerCase()
    ) {
      showSnackbar('From Region and To Region cannot be the same', 'error');
      return;
    }

    if (mergedForValidation.sales_start && mergedForValidation.sales_end && mergedForValidation.sales_start >= mergedForValidation.sales_end) {
      showSnackbar('Sales Start must be before Sales End', 'error');
      return;
    }

    if (mergedForValidation.travel_start && mergedForValidation.travel_end && mergedForValidation.travel_start >= mergedForValidation.travel_end) {
      showSnackbar('Travel Start must be before Travel End', 'error');
      return;
    }

    if (mergedForValidation.blackout_startdate && mergedForValidation.blackout_enddate && mergedForValidation.blackout_startdate >= mergedForValidation.blackout_enddate) {
      showSnackbar('Blackout Start must be before Blackout End', 'error');
      return;
    }

    try {
      setSaveLoading(true);
      const updatedDeal = { ...originalDeal, ...editedData };
      delete updatedDeal.created_at;
      delete updatedDeal.updated_at;

      const updatedRbdCode = String(updatedDeal.rbd_type ?? '').toLowerCase() === 'all'
        ? 'A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W,X,Y,Z'
        : (updatedDeal.rbd_code || '');

      const updatePayload = {
        id: updatedDeal.id,
        scheme_name: updatedDeal.scheme_name || '',
        channel: updatedDeal.channel || '',
        supplier_type: expandStaticOption(updatedDeal.supplier_type, 'ALL', 'GDS,LCC,NDC') || updatedDeal.supplier_type || '',
        supplier_name: updatedDeal.supplier_name || '',
        supplier: updatedDeal.supplier || '',
        office_id: updatedDeal.office_id || '',
        content_type: expandStaticOption(updatedDeal.content_type, 'ALL', 'Published,Private'),
        currency: updatedDeal.currency || '',
        route_type: expandStaticOption(updatedDeal.route_type, 'All', 'International,Domestic'),
        applicable_route_type: expandStaticOption(updatedDeal.applicable_route_type, 'Both', 'OneWay,RoundTrip'),
        from_country: toArray(updatedDeal.from_country),
        to_country: toArray(updatedDeal.to_country),
        from_city: toArray(updatedDeal.from_city),
        to_city: toArray(updatedDeal.to_city),
        from_region: toArray(updatedDeal.from_region),
        to_region: toArray(updatedDeal.to_region),
        excluded_from_country: toArray(updatedDeal.excluded_from_country),
        excluded_to_country: toArray(updatedDeal.excluded_to_country),
        excluded_from_city: toArray(updatedDeal.excluded_from_city),
        excluded_to_city: toArray(updatedDeal.excluded_to_city),
        airline_name: updatedDeal.airline_name || '',
        cabin_class: updatedDeal.cabin_class || '',
        rbd_type: updatedDeal.rbd_type || '',
        rbd_code: toArray(updatedRbdCode),
        schema_type: updatedDeal.schema_type || '',
        fare_tourcode: updatedDeal.fare_tourcode || '',
        farebase: updatedDeal.farebase || '',
        farebase_matchtype: updatedDeal.farebase_matchtype || '',
        code_share: updatedDeal.code_share || '',
        sales_start: updatedDeal.sales_start || '',
        sales_end: updatedDeal.sales_end || '',
        travel_start: updatedDeal.travel_start || '',
        travel_end: updatedDeal.travel_end || '',
        blackout_start: updatedDeal.blackout_startdate || '',
        blackout_end: updatedDeal.blackout_enddate || '',
        payout_onward: payoutOnwardRows
          .filter((r) => r.payout_onward_type)
          .map((r) => ({
            type: r.payout_onward_type,
            amount: r.payout_onward_amount,
            chargeValue: r.payout_chargevalue,
            paxtype: expandStaticOption(r.payout_onward_value, 'ALL', 'ADULT,CHILD'),
          })),
        payout_return: payoutReturnRows
          .filter((r) => r.payout_return_type)
          .map((r) => ({
            type: r.payout_return_type,
            amount: r.payout_return_amount,
            chargeValue: r.payout_return_chargevalue,
            paxtype: expandStaticOption(r.payout_return_value, 'ALL', 'ADULT,CHILD'),
          })),
        is_active: updatedDeal.is_active ?? 1,
      };

      await updateDropnetRuleAPI(updatePayload);

      showSnackbar('Dropnet updated successfully', 'success');

      setViewDataMap((prev) => {
        const newMap = { ...prev };
        delete newMap[id];
        return newMap;
      });

      await fetchDealCodes();
      setEditRowId(null);
      setEditedData({});
    } catch (err) {
      console.error('Failed to update dropnet:', err);
      showSnackbar('Failed to update dropnet', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  /* ================= ADD NEW TOGGLE ================= */
  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditRowId(null);
    setEditedData({});
    setOpenRowId(null);
    setNewDeal({ ...emptyDeal });
    setNewDealErrors({});
    setPayoutOnwardRows([{ ...EMPTY_PAYOUT_ONWARD_ROW }]);
    setPayoutReturnRows([{ ...EMPTY_PAYOUT_RETURN_ROW }]);
  };

  const handleCancelAddNew = () => {
    setIsAddingNew(false);
    setNewDealErrors({});
    setNewDeal({ ...emptyDeal });
    setPayoutOnwardRows([{ ...EMPTY_PAYOUT_ONWARD_ROW }]);
    setPayoutReturnRows([{ ...EMPTY_PAYOUT_RETURN_ROW }]);
    setAutocompleteInputDraft({});
    setMultiSelectQuery({});
  };

  const handleNewDealChange = (field: keyof DealCodeData, value: any) => {
    setNewDeal((prev) => ({ ...prev, [field]: value }));
    setNewDealErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  /* ================= ALL-EXPANSION HELPERS ================= */
  const expandIfAll = (value: string | undefined | null, list: string[]): string => {
    if (String(value ?? '').toLowerCase() === 'all') {
      return list.filter((v) => v.toLowerCase() !== 'all').join(',');
    }
    return value || '';
  };

  const expandAllMinusExcluded = (
    value: string | undefined | null,
    list: string[],
    excluded: string | null | undefined,
  ): string => {
    if (String(value ?? '').toLowerCase() !== 'all') return value || '';
    const excludedSet = new Set(
      excluded ? excluded.split(',').map((s) => s.trim()).filter(Boolean) : []
    );
    return list.filter((v) => v.toLowerCase() !== 'all' && !excludedSet.has(v)).join(',');
  };

  const expandStaticOption = (value: string | undefined | null, triggerValue: string, expansion: string): string => {
    if (String(value ?? '').toLowerCase() === triggerValue.toLowerCase()) return expansion;
    return value || '';
  };

  const collapseToAllLabel = (value: string | number | null | undefined): string | number | null | undefined => {
    if (typeof value !== 'string' || !value) return value;
    const normalized = value.replace(/,\s*/g, ',');
    for (const [expanded, label] of STATIC_ALL_EXPANSIONS) {
      if (normalized === expanded) return label;
    }
    if (locationsCache.current) {
      const allCountries = locationsCache.current.countries.filter((c) => c.name.toLowerCase() !== 'all').map((c) => c.name).join(',');
      if (normalized === allCountries) return 'All';
      const allCities = locationsCache.current.cities.filter((c) => c.name.toLowerCase() !== 'all').map((c) => c.name).join(',');
      if (normalized === allCities) return 'All';
    }
    return value;
  };

  /* ================= MANDATORY VALIDATION ================= */
  const validateMandatoryForCreate = (): boolean => {
    const errs: Record<string, string> = {};
    const R = 'Required';

    if (!newDeal.scheme_name?.trim()) errs.scheme_name = R;
    if (!newDeal.channel) errs.channel = R;
    if (!newDeal.supplier_type) errs.supplier_type = R;
    if (!newDeal.supplier_name) errs.supplier_name = R;
    if (newDeal.supplier_name && !newDeal.supplier) errs.supplier = R;
    if (!newDeal.currency?.trim()) errs.currency = R;
    if (!newDeal.route_type) errs.route_type = R;
    if (!newDeal.applicable_route_type) errs.applicable_route_type = R;
    if (!newDeal.airline_name?.trim()) errs.airline_name = R;
    if (!newDeal.from_region) errs.from_region = R;
    if (!newDeal.to_region) errs.to_region = R;
    if (!newDeal.from_country) errs.from_country = R;
    if (!newDeal.to_country) errs.to_country = R;
    if (!newDeal.from_city) errs.from_city = R;
    if (!newDeal.to_city) errs.to_city = R;
    if (!newDeal.cabin_class?.trim()) errs.cabin_class = R;
    if (!newDeal.rbd_type) errs.rbd_type = R;
    if (newDeal.rbd_type !== 'All' && !newDeal.rbd_code?.trim()) errs.rbd_code = R;
    if (!newDeal.sales_start) errs.sales_start = R;
    if (!newDeal.sales_end) errs.sales_end = R;
    if (!newDeal.travel_start) errs.travel_start = R;
    if (!newDeal.travel_end) errs.travel_end = R;
    if (newDeal.is_active !== 0 && newDeal.is_active !== 1) errs.is_active = R;

    payoutOnwardRows.forEach((row, i) => {
      if (!row.payout_onward_type) errs[`payout_onward_${i}_type`] = R;
      if (!row.payout_chargevalue) errs[`payout_onward_${i}_chargevalue`] = R;
      if (!row.payout_onward_value) errs[`payout_onward_${i}_value`] = R;
      if (!row.payout_onward_amount) errs[`payout_onward_${i}_amount`] = R;
    });

    payoutReturnRows.forEach((row, i) => {
      if (!row.payout_return_type) errs[`payout_return_${i}_type`] = R;
      if (!row.payout_return_chargevalue) errs[`payout_return_${i}_chargevalue`] = R;
      if (!row.payout_return_value) errs[`payout_return_${i}_value`] = R;
      if (!row.payout_return_amount) errs[`payout_return_${i}_amount`] = R;
    });

    setNewDealErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ================= SAVE NEW API ================= */
  const handleSaveNewDeal = async () => {
    if (!validateMandatoryForCreate()) {
      showSnackbar('These Fields are Mandatory', 'error');
      return;
    }

    if (
      newDeal.from_country && newDeal.to_country &&
      newDeal.from_country.toLowerCase() !== 'all' && newDeal.to_country.toLowerCase() !== 'all' &&
      newDeal.from_country.toLowerCase() === newDeal.to_country.toLowerCase()
    ) {
      showSnackbar('From Country and To Country cannot be the same', 'error');
      return;
    }

    if (
      newDeal.from_city && newDeal.to_city &&
      newDeal.from_city.toLowerCase() !== 'all' && newDeal.to_city.toLowerCase() !== 'all' &&
      newDeal.from_city.toLowerCase() === newDeal.to_city.toLowerCase()
    ) {
      showSnackbar('From City and To City cannot be the same', 'error');
      return;
    }

    if (
      newDeal.from_region && newDeal.to_region &&
      String(newDeal.from_region).toLowerCase() !== 'all' && String(newDeal.to_region).toLowerCase() !== 'all' &&
      String(newDeal.from_region).toLowerCase() === String(newDeal.to_region).toLowerCase()
    ) {
      showSnackbar('From Region and To Region cannot be the same', 'error');
      return;
    }

    if (newDeal.sales_start && newDeal.sales_end && newDeal.sales_start >= newDeal.sales_end) {
      showSnackbar('Sales Start must be before Sales End', 'error');
      return;
    }

    if (newDeal.travel_start && newDeal.travel_end && newDeal.travel_start >= newDeal.travel_end) {
      showSnackbar('Travel Start must be before Travel End', 'error');
      return;
    }

    if (newDeal.blackout_startdate && newDeal.blackout_enddate && newDeal.blackout_startdate >= newDeal.blackout_enddate) {
      showSnackbar('Blackout Start must be before Blackout End', 'error');
      return;
    }

    setNewDealErrors({});

    try {
      setSaveLoading(true);

      const rbdCodeExpanded = String(newDeal.rbd_type ?? '').toLowerCase() === 'all'
        ? 'A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W,X,Y,Z'
        : (newDeal.rbd_code || '');

      const payload = {
        scheme_name: newDeal.scheme_name || '',
        channel: newDeal.channel || '',
        supplier_type: expandStaticOption(newDeal.supplier_type, 'ALL', 'GDS,LCC,NDC') || newDeal.supplier_type || '',
        supplier_name: newDeal.supplier_name || '',
        supplier: newDeal.supplier || '',
        office_id: newDeal.office_id || '',
        content_type: expandStaticOption(newDeal.content_type, 'ALL', 'Published, Private'),
        currency: newDeal.currency || '',
        route_type: expandStaticOption(newDeal.route_type, 'All', 'International, Domestic'),
        applicable_route_type: expandStaticOption(newDeal.applicable_route_type, 'Both', 'OneWay, RoundTrip'),
        from_country: toArray(newDeal.from_country),
        to_country: toArray(newDeal.to_country),
        from_city: toArray(newDeal.from_city),
        to_city: toArray(newDeal.to_city),
        from_region: toArray(newDeal.from_region),
        to_region: toArray(newDeal.to_region),
        excluded_from_country: toArray(newDeal.excluded_from_country),
        excluded_to_country: toArray(newDeal.excluded_to_country),
        excluded_from_city: toArray(newDeal.excluded_from_city),
        excluded_to_city: toArray(newDeal.excluded_to_city),
        airline_name: newDeal.airline_name || '',
        cabin_class: newDeal.cabin_class || '',
        rbd_type: newDeal.rbd_type || '',
        rbd_code: toArray(rbdCodeExpanded),
        schema_type: newDeal.schema_type || '',
        fare_tourcode: newDeal.fare_tourcode || '',
        farebase: newDeal.farebase || '',
        farebase_matchtype: newDeal.farebase_matchtype || '',
        code_share: newDeal.code_share || '',
        sales_start: newDeal.sales_start || '',
        sales_end: newDeal.sales_end || '',
        travel_start: newDeal.travel_start || '',
        travel_end: newDeal.travel_end || '',
        blackout_start: newDeal.blackout_startdate || '',
        blackout_end: newDeal.blackout_enddate || '',
        payout_onward: payoutOnwardRows
          .filter((r) => r.payout_onward_type)
          .map((r) => ({
            type: r.payout_onward_type,
            amount: r.payout_onward_amount,
            chargeValue: r.payout_chargevalue,
            paxtype: expandStaticOption(r.payout_onward_value, 'ALL', 'ADULT, CHILD'),
          })),
        payout_return: payoutReturnRows
          .filter((r) => r.payout_return_type)
          .map((r) => ({
            type: r.payout_return_type,
            amount: r.payout_return_amount,
            chargeValue: r.payout_return_chargevalue,
            paxtype: expandStaticOption(r.payout_return_value, 'ALL', 'ADULT, CHILD'),
          })),
        is_active: newDeal.is_active ?? 1,
      };

      await saveDropnetRuleAPI(payload);

      showSnackbar('Dropnet saved successfully', 'success');
      handleCancelAddNew();
      await fetchDealCodes();
    } catch (err) {
      console.error('Failed to save dropnet:', err);
      showSnackbar('Failed to save dropnet', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const isEditing = (id: number) => editRowId === id;

  /* ================= FORM FIELD RENDERER ================= */
  const renderField = (
    label: string,
    field: keyof DealCodeData,
    data: Partial<DealCodeData>,
    onChange: (field: keyof DealCodeData, value: any) => void,
    options?: { type?: string; selectOptions?: { value: string; label: string }[]; errors?: Record<string, string>; disabled?: boolean }
  ) => {
    const { type = 'text', selectOptions, errors, disabled } = options || {};
    const hasError = !!(errors && errors[field]);
    const fieldValue = data[field];
    const displayValue = Array.isArray(fieldValue) ? '' : (fieldValue ?? '');
    return (
      <Box className="form-control inputFieldWrap">
        <label>{label}</label>
        {selectOptions ? (
          <FormControl
            sx={{ minWidth: 260, ...(hasError && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }}
            className="commondropdownWrap"
          >
            <Select
              className="commondropdown superdropdown"
              value={displayValue}
              onChange={(e) => onChange(field, e.target.value)}
              displayEmpty
              disabled={disabled}
              renderValue={(selected) => {
                if (!selected) return <span style={{ color: '#888' }}>Select</span>;
                const match = selectOptions.find((opt) => opt.value === selected);
                return match ? match.label : String(selected);
              }}
            >
              <MenuItem value="">
                <span style={{ color: '#888' }}>Select</span>
              </MenuItem>
              {selectOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <input
            type={type}
            className="inputField"
            style={hasError ? { border: '1px solid #d32f2f' } : undefined}
            value={displayValue}
            onChange={(e) => onChange(field, e.target.value)}
          />
        )}
        {hasError && (
          <span className='errorNewSupplier'>{errors![field]}</span>
        )}
      </Box>
    );
  };

  const renderViewFieldWithShowAll = (label: string, value?: string | number | null | string[], maxItems: number = 2) => {
    const strValue: string | null = Array.isArray(value)
      ? (value as string[]).join(',')
      : (typeof value === 'string' ? value.trim() : null);
    const items = strValue ? strValue.split(',').map((s) => s.trim()).filter(Boolean) : [];
    const containerSx = { flex: '1 1 calc(25% - 16px)', minWidth: '180px', maxWidth: '195px' };

    if (items.length === 0) {
      return (
        <Box key={label} className="form-control inputFieldWrap" sx={containerSx}>
          <label>{label}</label>
          <Typography sx={{ fontSize: '14px', color: '#333' }}>-</Typography>
        </Box>
      );
    }

    if (items.length <= maxItems) {
      const displayText = items.join(', ');
      return (
        <Box key={label} className="form-control inputFieldWrap" sx={containerSx}>
          <label>{label}</label>
          <Tooltip title={displayText} placement="top" arrow>
            <Typography sx={{ fontSize: '14px', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayText}
            </Typography>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box key={label} className="form-control inputFieldWrap" sx={containerSx}>
        <label>{label}</label>
        <Typography
          component="span"
          onClick={() => openShowAllModal(label, strValue)}
          sx={{
            fontSize: '14px',
            color: '#272727',
            cursor: 'pointer',
            fontWeight: 500,
            textDecoration: 'none',
            transition: 'text-decoration 0.2s ease',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Show All
        </Typography>
      </Box>
    );
  };

  /* ================= FORM SECTIONS (reusable for modal & edit) ================= */
  const renderFormFields = (
    data: Partial<DealCodeData>,
    onChange: (field: keyof DealCodeData, value: any) => void,
    errors?: Record<string, string>,
    onwardRows?: PayoutOnwardRow[],
    onAddOnwardRow?: () => void,
    onRemoveOnwardRow?: (index: number) => void,
    onOnwardRowChange?: (index: number, field: keyof PayoutOnwardRow, value: string) => void,
    returnRows?: PayoutReturnRow[],
    onAddReturnRow?: () => void,
    onRemoveReturnRow?: (index: number) => void,
    onReturnRowChange?: (index: number, field: keyof PayoutReturnRow, value: string) => void,
  ) => {
    const channelOptions = [
      { value: 'B2C', label: 'B2C' },
      { value: 'B2B', label: 'B2B' },
      { value: 'B2B2C', label: 'B2B2C' },
    ];
    const SUPPLIER_TYPE_ITEMS = ['GDS', 'LCC', 'NDC'];
    // Expand "ALL" from existing DB records to the full list
    const rawSupplierType = String(data.supplier_type ?? '');
    const supplierTypeSelected: string[] = rawSupplierType.toLowerCase() === 'all'
      ? [...SUPPLIER_TYPE_ITEMS]
      : rawSupplierType.split(',').map((s) => s.trim()).filter(Boolean);
    const allSupplierTypesSelected = SUPPLIER_TYPE_ITEMS.every((t) => supplierTypeSelected.includes(t));

    const handleSupplierTypeChange = (value: string) => {
      const current = supplierTypeSelected;
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      onChange('supplier_type', updated.join(','));
    };

    const handleSupplierTypeAll = () => {
      if (allSupplierTypesSelected) {
        onChange('supplier_type', '');
      } else {
        onChange('supplier_type', SUPPLIER_TYPE_ITEMS.join(','));
      }
    };
    const contentTypeOptions = [
      { value: 'ALL', label: 'ALL' },
      { value: 'Published', label: 'Published' },
      { value: 'Private', label: 'Private' },
    ];
    const routeTypeOptions = [
      { value: 'All', label: 'All' },
      { value: 'International', label: 'International' },
      { value: 'Domestic', label: 'Domestic' },
    ];
    const applicableRouteOptions = [
      { value: 'OneWay', label: 'One Way' },
      { value: 'RoundTrip', label: 'Round Trip' },
      { value: 'Both', label: 'Both' },
    ];
    const codeShareOptions = [
      { value: 'INCLUDED', label: 'Included' },
      { value: 'EXCLUDED', label: 'Excluded' },
    ];
    const payoutTypeOptions = [
      { value: 'PERCENTAGE', label: 'Percentage' },
      { value: 'VALUE', label: 'Value' },
    ];
    const payoutAdtOptions = [
      { value: 'ALL', label: 'All' },
      { value: 'ADULT', label: 'Adult' },
      { value: 'CHILD', label: 'Child' },
    ];
    const statusOptions = [
      { value: '1', label: 'Active' },
      { value: '0', label: 'Inactive' },
    ];
    const supplierNameOptions = [
      { value: 'AMADEUS', label: 'AMADEUS' },
      { value: 'API', label: 'API' },
      { value: 'BOTH', label: 'BOTH' },
    ];
    const supplierOptions = Array.from(
      new Map(
        availableSuppliers
          .filter((s) =>
            data.supplier_name === 'BOTH'
              ? s.supplier_source === 'AMADEUS' || s.supplier_source === 'API'
              : s.supplier_source === data.supplier_name
          )
          .map((s) => [s.supplier_name, { value: s.supplier_name, label: s.supplier_name }])
      ).values()
    );
    const handleSupplierSourceChange = (field: keyof DealCodeData, value: any) => {
      onChange(field, value);
      onChange('supplier', '');
      onChange('office_id', '');
    };
    const handleSupplierChange = (field: keyof DealCodeData, value: any) => {
      onChange(field, value);
      const match = availableSuppliers.find(
        (s) => s.supplier_name === value && (
          !data.supplier_name ||
          data.supplier_name === 'BOTH' ||
          s.supplier_source === data.supplier_name
        )
      );
      onChange('office_id', match?.office_id ?? '');
    };
    const rbdTypeOptions = [
      { value: 'All', label: 'All' },
      { value: 'Specific', label: 'Specific' },
    ];
    const farebaseMatchOptions = [
      { value: 'StartsWith', label: 'Starts With' },
      { value: 'Contains', label: 'Contains' },
      { value: 'Exact', label: 'Exact' },
    ];

    const handleFieldChange = (field: keyof DealCodeData, value: any) => {
      onChange(field, value);
      if (field === 'from_country' && String(value).toLowerCase() !== 'all') onChange('excluded_from_country', null);
      if (field === 'to_country' && String(value).toLowerCase() !== 'all') onChange('excluded_to_country', null);
      if (field === 'from_country') { onChange('from_city', ''); onChange('excluded_from_city', null); }
      if (field === 'to_country') { onChange('to_city', ''); onChange('excluded_to_city', null); }
      if (field === 'from_city' && String(value).toLowerCase() !== 'all') onChange('excluded_from_city', null);
      if (field === 'to_city' && String(value).toLowerCase() !== 'all') onChange('excluded_to_city', null);

      if (field === 'from_region' || field === 'to_region') {
        const from = field === 'from_region' ? String(value) : String(data.from_region ?? '');
        const to = field === 'to_region' ? String(value) : String(data.to_region ?? '');
        if (from && to && from.toLowerCase() !== 'all' && to.toLowerCase() !== 'all' && from.toLowerCase() === to.toLowerCase()) {
          showSnackbar('From Region and To Region cannot be the same', 'error');
        }
      }

    };

    const isAllSelected = (field: keyof DealCodeData) =>
      String(data[field] ?? '').toLowerCase() === 'all';

    return (
      <>
        {/* Scheme Info */}
        <Box className="feeModalForm">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f7fcff',  }}>
            <Typography variant="h5" sx={{background: 'transparent'}}>Scheme Info</Typography>
            <Typography component="h5" className='clearSection' onClick={() => { ['scheme_name','channel','supplier_type','supplier_name','supplier','office_id','content_type','currency'].forEach((f) => onChange(f as keyof DealCodeData, '')); }}>Clear All</Typography>
          </Box>
        <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {renderField('Scheme Name', 'scheme_name', data, onChange, { errors })}
          {renderField('Channel', 'channel', data, onChange, { selectOptions: channelOptions, errors })}
          {/* Supplier Type — multi-select */}
          <Box className="form-control inputFieldWrap">
            <label>Supplier Type</label>
            <FormControl sx={{ minWidth: 260, ...(errors && errors.supplier_type && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }} className="commondropdownWrap">
              <Select
                className="commondropdown superdropdown"
                multiple
                value={supplierTypeSelected}
                displayEmpty
                renderValue={(selected) => {
                  if ((selected as string[]).length === 0) return <span style={{ color: '#888' }}>Select</span>;
                  if (SUPPLIER_TYPE_ITEMS.every((t) => (selected as string[]).includes(t))) return 'ALL';
                  return (selected as string[]).join(', ');
                }}
              >
                <MenuItem onClick={handleSupplierTypeAll} sx={{ py: 0.5 }}>
                  <Checkbox size="small" checked={allSupplierTypesSelected} indeterminate={supplierTypeSelected.length > 0 && !allSupplierTypesSelected} sx={{ color: '#272727', '&.Mui-checked': { color: '#272727' }, '&.MuiCheckbox-indeterminate': { color: '#272727' } }} />
                  <ListItemText primary="ALL" />
                </MenuItem>
                {SUPPLIER_TYPE_ITEMS.map((opt) => (
                  <MenuItem key={opt} value={opt} onClick={() => handleSupplierTypeChange(opt)} sx={{ py: 0.5 }}>
                    <Checkbox size="small" checked={supplierTypeSelected.includes(opt)} sx={{ color: '#272727', '&.Mui-checked': { color: '#272727' } }} />
                    <ListItemText primary={opt} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors && errors['supplier_type'] && (
              <span className="errorNewSupplier">{errors['supplier_type']}</span>
            )}
          </Box>
          {renderField('Supplier Source', 'supplier_name', data, handleSupplierSourceChange, { selectOptions: supplierNameOptions, errors })}
          {renderField('Supplier Name', 'supplier', data, handleSupplierChange, { selectOptions: supplierOptions, errors, disabled: !data.supplier_name })}
          {renderField('Office ID', 'office_id', data, onChange, { errors })}
          {renderField('Content Type', 'content_type', data, onChange, { selectOptions: contentTypeOptions, errors })}
          {renderField('Currency Type', 'currency', data, onChange, { errors })}
          {renderField('Schema Type', 'schema_type', data, onChange)}
        </Box>
        </Box>

        {/* Route Info */}
        <Box className="feeModalForm routeInfoSection">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f7fcff' }}>
            <Typography variant="h5" sx={{ background: 'transparent' }}>Route Info</Typography>
            <Typography component="h5" className='clearSection' onClick={() => { ['route_type','applicable_route_type','from_region','to_region','from_country','to_country','from_city','to_city','excluded_from_country','excluded_to_country','excluded_from_city','excluded_to_city','airline_name'].forEach((f) => onChange(f as keyof DealCodeData, f.includes('region') || f.includes('excluded') ? null : '')); }}>Clear All</Typography>
          </Box>
        <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, pb: 2 }}>
          {renderField('Route Type', 'route_type', data, onChange, { selectOptions: routeTypeOptions, errors })}
          {renderField('Applicable Route Type', 'applicable_route_type', data, onChange, { selectOptions: applicableRouteOptions, errors })}
          {renderAutocompleteField('Airline Name', 'airline_name', data, onChange, errors)}
        </Box>
        <Box className="feeInputFieldWrap separateLine" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {renderMultiSelectAutocomplete('From Region', 'from_region', data, handleFieldChange, errors)}
          {renderMultiSelectAutocomplete('To Region', 'to_region', data, handleFieldChange, errors)}
        </Box>
        <Box className="feeInputFieldWrap separateLine" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {renderMultiSelectAutocomplete('From Country', 'from_country', data, handleFieldChange, errors)}
          {renderMultiSelectAutocomplete('To Country', 'to_country', data, handleFieldChange, errors)}
          {renderMultiSelectAutocomplete('From City', 'from_city', data, handleFieldChange, errors)}
          {renderMultiSelectAutocomplete('To City', 'to_city', data, handleFieldChange, errors)}
        </Box>
        <Box className="feeInputFieldWrap separateLine" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {renderMultiSelectAutocomplete('Excluded From Country', 'excluded_from_country', data, onChange, undefined, !isAllSelected('from_country'))}
          {renderMultiSelectAutocomplete('Excluded To Country', 'excluded_to_country', data, onChange, undefined, !isAllSelected('to_country'))}
          {renderMultiSelectAutocomplete('Excluded From City', 'excluded_from_city', data, onChange, undefined, !isAllSelected('from_city'))}
          {renderMultiSelectAutocomplete('Excluded To City', 'excluded_to_city', data, onChange, undefined, !isAllSelected('to_city'))}
        </Box>
        </Box>

        {/* Fare Details */}
        <Box className="feeModalForm">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f7fcff' }}>
            <Typography variant="h5" sx={{ background: 'transparent' }}>Fare Details</Typography>
            <Typography component="h5" className='clearSection' onClick={() => { ['cabin_class','rbd_type','rbd_code','schema_type','fare_tourcode','farebase','farebase_matchtype','code_share'].forEach((f) => onChange(f as keyof DealCodeData, '')); }}>Clear All</Typography>
          </Box>
        <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {renderField('Cabin Class', 'cabin_class', data, onChange, { selectOptions: [{ value: 'First', label: 'First' }, { value: 'Premium Economy', label: 'Premium Economy' }, { value: 'Business', label: 'Business' }, { value: 'Economy', label: 'Economy' }], errors })}
          <Box className="form-control inputFieldWrap">
            <label>RBD Type</label>
            <FormControl sx={{ minWidth: 260, ...(errors && errors.rbd_type && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }} className="commondropdownWrap">
              <Select
                className="commondropdown superdropdown"
                value={data.rbd_type ?? ''}
                onChange={(e) => {
                  onChange('rbd_type', e.target.value);
                  if (e.target.value === 'All') onChange('rbd_code', '');
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return <span style={{ color: '#888' }}>Select</span>;
                  const match = rbdTypeOptions.find((opt) => opt.value === selected);
                  return match ? match.label : String(selected);
                }}
              >
                <MenuItem value="">
                  <span style={{ color: '#888' }}>Select</span>
                </MenuItem>
                {rbdTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors && errors.rbd_type && (
              <span className='errorNewSupplier'>{errors.rbd_type}</span>
            )}
          </Box>
          <Box className="form-control inputFieldWrap">
            <label>RBD Code</label>
            <FormControl sx={{ minWidth: 260, ...(errors && errors.rbd_code && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }} className="commondropdownWrap">
              {(() => {
                const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
                const selectedArr = data.rbd_code ? String(data.rbd_code).split(',').map((s) => s.trim()).filter(Boolean) : [];
                const allSelected = allLetters.every((l) => selectedArr.includes(l));
                return (
                  <Select
                    className="commondropdown superdropdown"
                    multiple
                    value={selectedArr}
                    onChange={(e) => {
                      const val = e.target.value as string[];
                      if (val.includes('All')) {
                        onChange('rbd_code', allSelected ? '' : allLetters.join(', '));
                      } else {
                        onChange('rbd_code', val.length > 0 ? val.join(', ') : '');
                      }
                    }}
                    disabled={data.rbd_type === 'All'}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || (Array.isArray(selected) && (selected as string[]).length === 0)) {
                        return <span style={{ color: '#888' }}>{data.rbd_type === 'All' ? 'Disabled' : 'Select'}</span>;
                      }
                      if (allSelected) return 'All';
                      return Array.isArray(selected) ? (selected as string[]).join(', ') : String(selected);
                    }}
                  >
                    <MenuItem key="All" value="All" sx={{ py: 0.5 }}>
                      <Checkbox checked={allSelected} size="small" sx={{ color: '#272727', '&.Mui-checked': { color: '#272727' } }} />
                      <ListItemText primary="All" />
                    </MenuItem>
                    {allLetters.map((letter) => (
                      <MenuItem key={letter} value={letter} sx={{ py: 0.5 }}>
                        <Checkbox checked={selectedArr.includes(letter)} size="small" sx={{ color: '#272727', '&.Mui-checked': { color: '#272727' } }} />
                        <ListItemText primary={letter} />
                      </MenuItem>
                    ))}
                  </Select>
                );
              })()}
            </FormControl>
          </Box>
          
          {renderField('Fare Tourcode', 'fare_tourcode', data, onChange)}
          {renderField('Farebase', 'farebase', data, onChange)}
          {renderField('Farebase Match Type', 'farebase_matchtype', data, onChange, { selectOptions: farebaseMatchOptions })}
          {renderField('Code Share', 'code_share', data, onChange, { selectOptions: codeShareOptions })}
        </Box>
        </Box>

        {/* Date Range */}
        {(() => {
          const handleDateChange = (field: keyof DealCodeData, value: any) => {
            onChange(field, value);
            const updated = { ...data, [field]: value };
            if (updated.sales_start && updated.sales_end && updated.sales_start >= updated.sales_end) {
              showSnackbar('Sales Start must be before Sales End', 'error');
            } else if (updated.travel_start && updated.travel_end && updated.travel_start >= updated.travel_end) {
              showSnackbar('Travel Start must be before Travel End', 'error');
            } else if (updated.blackout_startdate && updated.blackout_enddate && updated.blackout_startdate >= updated.blackout_enddate) {
              showSnackbar('Blackout Start must be before Blackout End', 'error');
            }
          };
          return (
            <Box className="feeModalForm dateRangeSection">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f7fcff' }}>
                <Typography variant="h5" sx={{ background: 'transparent' }}>Date Range</Typography>
                <Typography component="h5" className='clearSection' onClick={() => { ['sales_start','sales_end','travel_start','travel_end','blackout_startdate','blackout_enddate'].forEach((f) => onChange(f as keyof DealCodeData, '')); }}>Clear All</Typography>
              </Box>
              <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {renderField('Sales Start', 'sales_start', data, handleDateChange, { type: 'date', errors })}
                {renderField('Sales End', 'sales_end', data, handleDateChange, { type: 'date', errors })}
              </Box>
              <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {renderField('Travel Start', 'travel_start', data, handleDateChange, { type: 'date', errors })}
                {renderField('Travel End', 'travel_end', data, handleDateChange, { type: 'date', errors })}
              </Box>
              <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {renderField('Blackout Start', 'blackout_startdate', data, handleDateChange, { type: 'date' })}
                {renderField('Blackout End', 'blackout_enddate', data, handleDateChange, { type: 'date' })}
              </Box>
            </Box>
          );
        })()}


        {/* Payout Onward */}
        <Box className="feeModalForm payoutSection">
          <Box sx={{ position: 'relative'}}>
            <Typography variant="h5">Payout Onward</Typography>
            {(onwardRows ?? []).length < MAX_PAYOUT_ONWARD_ROWS && (
              <IconButton
                size="small"
                onClick={onAddOnwardRow}
                sx={{ color: '#272727', position: 'absolute', top: '10px', right: '10px', border: '1px solid #272727', borderRadius: '50%', width: 26, height: 26 }}
              >
                <Add fontSize="small" />
              </IconButton>
            )}
          </Box>
        {(onwardRows ?? [{ payout_onward_type: data.payout_onward_type ?? '', payout_chargevalue: data.payout_chargevalue ?? '', payout_onward_value: data.payout_onward_value ?? '', payout_onward_amount: '' }]).map((row, index, arr) => (
          <Box key={index} className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-end', mb: 2 }}>
            <Box className="form-control inputFieldWrap">
              <label>Payout Type</label>
              <FormControl sx={{ minWidth: 260, ...(errors && errors[`payout_onward_${index}_type`] && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }} className="commondropdownWrap">
                <Select
                  className="commondropdown superdropdown"
                  value={row.payout_onward_type}
                  onChange={(e) => onOnwardRowChange?.(index, 'payout_onward_type', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return <span style={{ color: '#888' }}>Select</span>;
                    const match = payoutTypeOptions.find((opt) => opt.value === selected);
                    return match ? match.label : String(selected);
                  }}
                >
                  <MenuItem value="">
                    <span style={{ color: '#888' }}>Select</span>
                  </MenuItem>
                  {payoutTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box className="form-control inputFieldWrap">
              <label>Payout Charge Value</label>
              <FormControl sx={{ minWidth: 260, ...(errors && errors[`payout_onward_${index}_chargevalue`] && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }} className="commondropdownWrap">
                <Select
                  className="commondropdown superdropdown"
                  value={row.payout_chargevalue}
                  onChange={(e) => onOnwardRowChange?.(index, 'payout_chargevalue', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return <span style={{ color: '#888' }}>Select</span>;
                    const chargeMap: Record<string, string> = { 'Base Fare': 'Base Fare', 'Total Fare': 'Total Fare' };
                    return chargeMap[selected as string] ?? String(selected);
                  }}
                >
                  <MenuItem value="">
                    <span style={{ color: '#888' }}>Select</span>
                  </MenuItem>
                  <MenuItem value="Base Fare">Base Fare</MenuItem>
                  <MenuItem value="Total Fare">Total Fare</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box className="form-control inputFieldWrap">
              <label>Pax Type</label>
              <FormControl sx={{ minWidth: 260, ...(errors && errors[`payout_onward_${index}_value`] && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }} className="commondropdownWrap">
                <Select
                  className="commondropdown superdropdown"
                  value={row.payout_onward_value}
                  onChange={(e) => onOnwardRowChange?.(index, 'payout_onward_value', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return <span style={{ color: '#888' }}>Select</span>;
                    const match = payoutAdtOptions.find((opt) => opt.value === selected);
                    return match ? match.label : String(selected);
                  }}
                >
                  <MenuItem value="">
                    <span style={{ color: '#888' }}>Select</span>
                  </MenuItem>
                  {payoutAdtOptions.map((opt) => {
                    const takenByOther = arr.some((r, i) => i !== index && r.payout_onward_value === opt.value);
                    return (
                      <MenuItem key={opt.value} value={opt.value} disabled={takenByOther}>
                        {opt.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box className="form-control inputFieldWrap">
              <label>Payout Amount</label>
              <input
                type="number"
                className="inputField"
                style={errors && errors[`payout_onward_${index}_amount`] ? { border: '1px solid #d32f2f' } : undefined}
                value={row.payout_onward_amount}
                onChange={(e) => onOnwardRowChange?.(index, 'payout_onward_amount', e.target.value)}
              />
            </Box>
            {arr.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', pb: '4px' }}>
                <IconButton
                  size="small"
                  onClick={() => onRemoveOnwardRow?.(index)}
                  sx={{ color: '#dc2626', border: '1px solid #dc2626', borderRadius: '50%', width: 36, height: 36 }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        ))}
        </Box>

        {/* Payout Return */}
        <Box className="feeModalForm payoutSection">
          <Box sx={{ position: 'relative'}}>
            <Typography variant="h5">Payout Return</Typography>
            {(returnRows ?? []).length < MAX_PAYOUT_RETURN_ROWS && (
              <IconButton
                size="small"
                onClick={onAddReturnRow}
                sx={{ color: '#272727', position: 'absolute', top: '10px', right: '10px', border: '1px solid #272727', borderRadius: '50%', width: 26, height: 26 }}
              >
                <Add fontSize="small" />
              </IconButton>
            )}
          </Box>
        {(returnRows ?? [{ payout_return_type: data.payout_return_type ?? '', payout_return_chargevalue: data.payout_chargevalue ?? '', payout_return_value: data.payout_return_value ?? '', payout_return_amount: '' }]).map((row, index, arr) => (
          <Box key={index} className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-end', mb: 2 }}>
            <Box className="form-control inputFieldWrap">
              <label>Payout Type</label>
              <FormControl sx={{ minWidth: 260, ...(errors && errors[`payout_return_${index}_type`] && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }} className="commondropdownWrap">
                <Select
                  className="commondropdown superdropdown"
                  value={row.payout_return_type}
                  onChange={(e) => onReturnRowChange?.(index, 'payout_return_type', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return <span style={{ color: '#888' }}>Select</span>;
                    const match = payoutTypeOptions.find((opt) => opt.value === selected);
                    return match ? match.label : String(selected);
                  }}
                >
                  <MenuItem value="">
                    <span style={{ color: '#888' }}>Select</span>
                  </MenuItem>
                  {payoutTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box className="form-control inputFieldWrap">
              <label>Payout Charge Value</label>
              <FormControl sx={{ minWidth: 260, ...(errors && errors[`payout_return_${index}_chargevalue`] && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }} className="commondropdownWrap">
                <Select
                  className="commondropdown superdropdown"
                  value={row.payout_return_chargevalue}
                  onChange={(e) => onReturnRowChange?.(index, 'payout_return_chargevalue', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return <span style={{ color: '#888' }}>Select</span>;
                    const chargeMap: Record<string, string> = { 'Base Fare': 'Base Fare', 'Total Fare': 'Total Fare' };
                    return chargeMap[selected as string] ?? String(selected);
                  }}
                >
                  <MenuItem value="">
                    <span style={{ color: '#888' }}>Select</span>
                  </MenuItem>
                  <MenuItem value="Base Fare">Base Fare</MenuItem>
                  <MenuItem value="Total Fare">Total Fare</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box className="form-control inputFieldWrap">
              <label>Pax Type</label>
              <FormControl sx={{ minWidth: 260, ...(errors && errors[`payout_return_${index}_value`] && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }} className="commondropdownWrap">
                <Select
                  className="commondropdown superdropdown"
                  value={row.payout_return_value}
                  onChange={(e) => onReturnRowChange?.(index, 'payout_return_value', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return <span style={{ color: '#888' }}>Select</span>;
                    const match = payoutAdtOptions.find((opt) => opt.value === selected);
                    return match ? match.label : String(selected);
                  }}
                >
                  <MenuItem value="">
                    <span style={{ color: '#888' }}>Select</span>
                  </MenuItem>
                  {payoutAdtOptions.map((opt) => {
                    const takenByOther = arr.some((r, i) => i !== index && r.payout_return_value === opt.value);
                    return (
                      <MenuItem key={opt.value} value={opt.value} disabled={takenByOther}>
                        {opt.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box className="form-control inputFieldWrap">
              <label>Payout Amount</label>
              <input
                type="number"
                className="inputField"
                style={errors && errors[`payout_return_${index}_amount`] ? { border: '1px solid #d32f2f' } : undefined}
                value={row.payout_return_amount}
                onChange={(e) => onReturnRowChange?.(index, 'payout_return_amount', e.target.value)}
              />
            </Box>
            {arr.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', pb: '4px' }}>
                <IconButton
                  size="small"
                  onClick={() => onRemoveReturnRow?.(index)}
                  sx={{ color: '#dc2626', border: '1px solid #dc2626', borderRadius: '50%', width: 36, height: 36 }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        ))}
        </Box>

        {/* Status */}
        <Box className="feeModalForm">
           <Typography variant="h5" gutterBottom>Status</Typography>
        <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '200px' }}>
            <label>Status</label>
            <FormControl sx={{ minWidth: 260, ...(errors && errors.is_active && { '& .commondropdown': { border: '1px solid #d32f2f !important' } }) }} className="commondropdownWrap">
              <Select
                className="commondropdown superdropdown"
                value={String(data.is_active ?? '')}
                onChange={(e) => onChange('is_active', e.target.value === '' ? '' : Number(e.target.value))}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected && selected !== '0') return <span style={{ color: '#888' }}>Select</span>;
                  return selected === '1' ? 'Active' : selected === '0' ? 'Inactive' : <span style={{ color: '#888' }}>Select</span>;
                }}
              >
                <MenuItem value="">
                  <span style={{ color: '#888' }}>Select</span>
                </MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        </Box>
      </>
    );
  };

  /* ================= VIEW DETAIL RENDERER ================= */
  const renderViewField = (label: string, value?: string | number | null, raw?: boolean, expandedValue?: string) => {
    const displayValue = (raw ? value : collapseToAllLabel(value)) ?? '-';
    // Clickable when: a comma-list was collapsed (e.g. "GDS,LCC,NDC" → "All"), OR an explicit expandedValue is provided (e.g. Supplier Source "Both" → "AMADEUS,API")
    const isClickable = (expandedValue != null) || (!raw && typeof value === 'string' && value.includes(',') && displayValue !== value);
    const modalRawValue = expandedValue ?? (value as string);
    return (
      <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '180px', maxWidth: '195px' }}>
        <label>{label}</label>
        {isClickable ? (
          <Typography
            component="span"
            onClick={() => openShowAllModal(label, modalRawValue)}
            sx={{
              fontSize: '14px',
              color: '#272727',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {displayValue}
          </Typography>
        ) : (
          <Typography
            component="div"
            sx={{
              fontSize: '14px',
              color: '#333',
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              '&::-webkit-scrollbar': { height: '3px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#bbb', borderRadius: '2px' },
            }}
          >
            {displayValue}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      {/* Header */}
      <Box className="headingBtnWrap" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Flight Dropnet
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddNew}
          sx={{
            backgroundColor: '#272727',
            '&:hover': { backgroundColor: '#1a1a1a' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Add New
        </Button>
      </Box>

      {/* Inline Form - Add New or Edit (slides above table) */}
      <Collapse in={isAddingNew || editRowId !== null} timeout={500}>
        <Box className="superAdminPage" sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isAddingNew ? 'Add New Dropnet Details' : 'Edit Dropnet Details'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={isAddingNew ? handleCancelAddNew : handleCancel}
                sx={{
                  borderColor: '#272727',
                  color: '#272727',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  textTransform: 'none',
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={isAddingNew ? handleSaveNewDeal : () => editRowId && handleUpdate(editRowId)}
                disabled={saveLoading}
                sx={{
                  backgroundColor: '#272727',
                  '&:hover': { backgroundColor: '#1a1a1a' },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {saveLoading ? <CircularProgress size={24} color="inherit" /> : isAddingNew ? 'Save' : 'Update'}
              </Button>
            </Box>
          </Box>
          <Box>
            {isAddingNew
              ? renderFormFields(newDeal, handleNewDealChange, newDealErrors, payoutOnwardRows, addPayoutOnwardRow, removePayoutOnwardRow, handlePayoutOnwardRowChange, payoutReturnRows, addPayoutReturnRow, removePayoutReturnRow, handlePayoutReturnRowChange)
              : renderFormFields(editedData, handleEditChange, undefined, payoutOnwardRows, addPayoutOnwardRow, removePayoutOnwardRow, handlePayoutOnwardRowChange, payoutReturnRows, addPayoutReturnRow, removePayoutReturnRow, handlePayoutReturnRowChange)
            }
          </Box>
        </Box>
      </Collapse>

      {/* Table */}
      <Box
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        className="supplierConfiguration"
      >
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: '75vh',
            overflowY: 'auto',
            backgroundColor: '#ffffff',
          }}
        >
          <Table stickyHeader>
            <TableHead
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: '#f1f3f4',
              }}
            >
              <TableRow
                sx={{
                  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
                }}
              >
                {['ID', 'SCHEME NAME', 'CHANNEL', 'SUPPLIER SOURCE', 'OFFICE ID', 'ROUTE TYPE', 'AIRLINE', 'STATUS', 'ACTIONS'].map(
                  (label) => (
                    <TableCell
                      key={label}
                      align="center"
                      sx={{
                        backgroundColor: '#f1f3f4',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                      }}
                    >
                      {label}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {dealCodes.map((deal) => {
                const isOpen = openRowId === deal.id;
                const editing = isEditing(deal.id);

                return (
                  <React.Fragment key={deal.id}>
                    {/* Main Row */}
                    <TableRow hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                      <TableCell align="center">{deal.id ?? '-'}</TableCell>
                      <TableCell align="center">{deal.scheme_name || '-'}</TableCell>
                      <TableCell align="center">
                        {deal.channel ? (
                          <Chip
                            label={deal.channel}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              backgroundColor: deal.channel === 'B2C' ? '#dbeafe' : '#fef3c7',
                              color: deal.channel === 'B2C' ? '#2563eb' : '#d97706',
                            }}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {deal.supplier_name === 'Both' ? (
                          <Typography
                            component="span"
                            onClick={() => openShowAllModal('Supplier Source', 'AMADEUS,API')}
                            sx={{ fontSize: '14px', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                          >
                            Both
                          </Typography>
                        ) : (deal.supplier_name || '-')}
                      </TableCell>
                      <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {deal.office_id || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {deal.route_type ? (
                          <Chip
                            label={deal.route_type}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              backgroundColor: deal.route_type === 'International' ? '#ede9fe' : deal.route_type === 'Domestic' ? '#d1fae5' : '#dbeafe',
                              color: deal.route_type === 'International' ? '#7c3aed' : deal.route_type === 'Domestic' ? '#059669' : '#1d4ed8',
                            }}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">{deal.airline_name || '-'}</TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            backgroundColor: deal.is_active === 1 ? '#d1fae5' : '#fee2e2',
                            color: deal.is_active === 1 ? '#059669' : '#dc2626',
                            border: `1px solid ${deal.is_active === 1 ? '#a7f3d0' : '#fecaca'}`,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          {deal.is_active === 1 ? 'Active' : 'Inactive'}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => toggleViewRow(deal.id)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={() => toggleEditRow(deal.id, deal)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Collapsible View Row */}
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 4, bgcolor: '#f5f5f5' }}>
                            {viewLoading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                <CircularProgress />
                              </Box>
                            ) : (
                              <Box className="feeModalForm extraStyle" sx={{ bgcolor: '#fff', p: 3, borderRadius: 2 }}>

                                {/* Scheme Info */}
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                   Scheme Info
                                </Typography>
                                <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                                  {renderViewField('Scheme Name', deal.scheme_name)}
                                  {renderViewField('Channel', deal.channel)}
                                  {renderViewField('Supplier Type', deal.supplier_type)}
                                  {renderViewField('Supplier Source', deal.supplier_name, false, deal.supplier_name === 'BOTH' ? 'AMADEUS,API' : undefined)}
                                  {renderViewField('Supplier Name', capitalize(deal.supplier), true)}
                                  {renderViewField('Office ID', deal.office_id)}
                                  {renderViewField('Content Type', deal.content_type)}
                                  {renderViewField('Currency Type', deal.currency)}
                                </Box>

                                {/* Route Info */}
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Route Info
                                </Typography>
                                <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                                  {renderViewField('Route Type', deal.route_type)}
                                  {renderViewField('Applicable Route', deal.applicable_route_type === 'Both' ? 'OneWay,RoundTrip' : deal.applicable_route_type)}
                                  {renderViewFieldWithShowAll('From Region', deal.from_region)}
                                  {renderViewFieldWithShowAll('To Region', deal.to_region)}
                                  {renderViewFieldWithShowAll('From Country', deal.from_country)}
                                  {renderViewFieldWithShowAll('Excluded From Country', deal.excluded_from_country)}
                                  {renderViewFieldWithShowAll('To Country', deal.to_country)}
                                  {renderViewFieldWithShowAll('Excluded To Country', deal.excluded_to_country)}
                                  {renderViewFieldWithShowAll('From City', deal.from_city)}
                                  {renderViewFieldWithShowAll('Excluded From City', deal.excluded_from_city)}
                                  {renderViewFieldWithShowAll('To City', deal.to_city)}
                                  {renderViewFieldWithShowAll('Excluded To City', deal.excluded_to_city)}
                                  {renderViewFieldWithShowAll('Airline Name', deal.airline_name)}
                                </Box>

                                {/* Fare Details */}
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Fare Details
                                </Typography>
                                <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                                  {renderViewField('Cabin Class', deal.cabin_class)}
                                  {renderViewField('RBD Type', deal.rbd_type)}
                                  {renderViewFieldWithShowAll('RBD Code', deal.rbd_code, 4)}
                                  {renderViewField('Schema Type', deal.schema_type)}
                                  {renderViewField('Fare Tourcode', deal.fare_tourcode)}
                                  {renderViewField('Farebase', deal.farebase)}
                                  {renderViewField('Farebase Match Type', deal.farebase_matchtype)}
                                  {renderViewField('Code Share', deal.code_share)}
                                </Box>

                                {/* Date Range */}
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Date Range
                                </Typography>
                                <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                                  {renderViewField('Sales Start', formatDate(deal.sales_start))}
                                  {renderViewField('Sales End', formatDate(deal.sales_end))}
                                  {renderViewField('Travel Start', formatDate(deal.travel_start))}
                                  {renderViewField('Travel End', formatDate(deal.travel_end))}
                                  {renderViewField('Blackout Start', formatDate(deal.blackout_startdate))}
                                  {renderViewField('Blackout End', formatDate(deal.blackout_enddate))}
                                </Box>

                                {/* Payout Onward */}
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Payout Onward
                                </Typography>
                                {(deal.payout_onward && deal.payout_onward.length > 0
                                  ? deal.payout_onward
                                  : deal.payout_onward_type
                                    ? [{ type: deal.payout_onward_type, chargeValue: deal.payout_chargevalue, paxtype: deal.payout_onward_value, amount: '' }]
                                    : []
                                ).map((row, idx) => (
                                  <Box key={idx} className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                    {renderViewField('Payout Type', row.type)}
                                    {renderViewField('Charge Value', row.chargeValue)}
                                    {renderViewField('Pax Type', row.paxtype)}
                                    {renderViewField('Amount', row.amount)}
                                  </Box>
                                ))}
                                {!(deal.payout_onward?.length) && !deal.payout_onward_type && (
                                  <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                    <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '180px', maxWidth: '195px' }}>
                                      <Typography sx={{ fontSize: '14px', color: '#333' }}>-</Typography>
                                    </Box>
                                  </Box>
                                )}

                                {/* Payout Return */}
                                <Typography variant="h6" sx={{ fontWeight: 600, mt: 2 }}>
                                  Payout Return
                                </Typography>
                                {(deal.payout_return && deal.payout_return.length > 0
                                  ? deal.payout_return
                                  : deal.payout_return_type
                                    ? [{ type: deal.payout_return_type, chargeValue: deal.payout_chargevalue, paxtype: deal.payout_return_value, amount: '' }]
                                    : []
                                ).map((row, idx) => (
                                  <Box key={idx} className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                    {renderViewField('Payout Type', row.type)}
                                    {renderViewField('Charge Value', row.chargeValue)}
                                    {renderViewField('Pax Type', row.paxtype)}
                                    {renderViewField('Amount', row.amount)}
                                  </Box>
                                ))}
                                {!(deal.payout_return?.length) && !deal.payout_return_type && (
                                  <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                                    <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '180px', maxWidth: '195px' }}>
                                      <Typography sx={{ fontSize: '14px', color: '#333' }}>-</Typography>
                                    </Box>
                                  </Box>
                                )}

                                {/* Status */}
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Status
                                </Typography>
                                <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                  {renderViewField('Status', deal.is_active === 1 ? 'Active' : 'Inactive')}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {!loading && dealCodes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8, fontWeight: 600, color: 'text.secondary' }}>
                    No dropnets data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Show All Modal */}
      <Dialog open={showAllModal.open} onClose={closeShowAllModal} maxWidth="xs" fullWidth>
        <Close onClick={closeShowAllModal} className='closeModal' />
        <DialogTitle sx={{ fontWeight: 600, fontSize: '16px' }}>
          {showAllModal.title}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ height: '300px', overflowY: 'auto', px: 2, py: 1 }}>
            {showAllModal.items.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  py: 1,
                  px: 1,
                  fontSize: '14px',
                  color: '#272727',
                  borderBottom: idx < showAllModal.items.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                {item.replace(/([a-z])([A-Z])/g, '$1 $2')}
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '50% !important',
          left: '50% !important',
          transform: 'translate(-50%, -50%) !important',
          '& .MuiSnackbarContent-root': {
            textAlign: 'center',
            minWidth: '200px',
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '1rem',
          }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FlightDropnet;
